/**
 * WebSocketContext - React Context for WebSocket multiplayer functionality
 * date_of_change: 2025-09-24
 *
 * Provides WebSocket service and authentication state throughout the React component tree.
 * Handles global WebSocket state, connection management, and multiplayer features.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useWebSocketAuth } from '../services/WebSocketAuthBridge';
import { leaveCombatSessionMembership } from '../services/combatSessionMembership';
import { useSessionRole } from '../hooks/useSessionRole';
import { logWebSocket } from '../utils/DebugLogger';

// Create WebSocket context
const WebSocketContext = createContext();

const createRealtimeMutationId = (eventName, userId = 'user') => {
  const uuid = typeof window !== 'undefined' && typeof window.crypto?.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return `${eventName}:${String(userId || 'user')}:${uuid}`;
};

/**
 * WebSocket Provider Component
 * Wraps the app to provide WebSocket functionality to all components
 */
export const WebSocketProvider = ({ children }) => {
  // WebSocket authentication and connection state
  const {
    webSocketStatus,
    isConnected,
    isConnecting,
    authError,
    connect,
    disconnect,
    reconnect,
    currentUser,
    webSocketService,
    isSignedIn,
    isAuthLoaded
  } = useWebSocketAuth();

  // Combat session state
  // @date_of_change: 2025-10-02 - Persist session ID across page refresh for GM role persistence
  const [currentSessionId, setCurrentSessionId] = useState(() => {
    try {
      return localStorage.getItem('current_session_id') || null;
    } catch (error) {
      console.warn('[WebSocketContext] Failed to load cached session ID:', error);
      return null;
    }
  });
  const [sessionParticipants, setSessionParticipants] = useState([]);
  const [sessionStatus, setSessionStatus] = useState('idle'); // 'idle', 'joining', 'joined', 'reconnecting', 'error'

  // @date_of_change: 2025-09-27 - Replace local role state with persistent session role hook
  // Use localStorage-cached role that survives page refresh
  const { role: userRole, isGM: isGameMaster, updateRole } = useSessionRole(currentSessionId);

  // Real-time data state
  const [combatState, setCombatState] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [stateRevision, setStateRevision] = useState(0);
  const [activeSceneId, setActiveSceneId] = useState(null);
  const [pendingMutationCount, setPendingMutationCount] = useState(0);
  const stateRevisionRef = useRef(0);

  useEffect(() => {
    const handleMutationQueue = (event) => {
      setPendingMutationCount(Math.max(0, Number(event?.detail?.pendingCount) || 0));
    };
    window.addEventListener('realtime-mutation-queue', handleMutationQueue);
    return () => window.removeEventListener('realtime-mutation-queue', handleMutationQueue);
  }, []);

  // Event listeners management
  const eventListenersRef = useRef(new Map());
  const joinInFlightRef = useRef({ sessionId: null, promise: null });

  /**
   * Join a combat session
   */
  const joinSession = useCallback(async (sessionId) => {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    if (currentSessionId === sessionId && (sessionStatus === 'joining' || sessionStatus === 'joined')) {
      logWebSocket('Join skipped - already attached to session:', sessionId, 'Status:', sessionStatus);
      return true;
    }

    if (joinInFlightRef.current.sessionId === sessionId && joinInFlightRef.current.promise) {
      logWebSocket('Join deduped - reusing in-flight join promise for session:', sessionId);
      return joinInFlightRef.current.promise;
    }

    const joinPromise = (async () => {
    // date_of_change: 2025-11-30 - Robust join: wait for WebSocket connection instead of failing fast
    if (!isConnected) {
      console.log('[WebSocketContext] WebSocket not connected; waiting for connection before joining');
      try {
        // Try to reuse stored token without hitting Clerk if possible
        if (!isConnecting && typeof connect === 'function') {
          await connect(true);
        }
        // Wait up to 7.5s for ready() promise from WebSocketService
        await Promise.race([
          webSocketService.ready(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('WebSocket ready timeout')), 7500))
        ]);
      } catch (e) {
        console.error('[WebSocketContext] Unable to establish WebSocket connection for join:', e?.message || e);
        throw new Error('WebSocket not connected');
      }
    }

    try {
      const recoveringExistingSession = sessionStatus === 'reconnecting';
      setSessionStatus(recoveringExistingSession ? 'reconnecting' : 'joining');
      logWebSocket('Joining combat session:', sessionId);

      // Join the session via WebSocket and capture role from acknowledgment (authoritative)
      // @date_of_change: 2025-09-30 - Enhanced debugging for role detection issue
      const joinResponse = await webSocketService.joinCombat(sessionId);
      console.log('[WebSocketContext] Join ACK received:', {
        joinResponse,
        responseOk: joinResponse?.ok,
        responseRole: joinResponse?.role,
        sessionId,
        currentUser: currentUser?.userId || 'none'
      });

      // Trust the socket role exclusively - never guess or derive from other sources
      // @date_of_change: 2025-09-26 - Always set role from server, default to player if missing
      const role = joinResponse?.role || 'player'; // Server should always provide role, fallback to player
      console.log('[WebSocketContext] Setting role:', role, 'from joinResponse:', joinResponse);
      // The ACK belongs to the requested room even when currentSessionId has
      // not committed yet. Persist against that explicit room to prevent a
      // GM-to-Player role reset on first navigation. date_of_change: 2026-08-18
      updateRole(role, sessionId);
      if (Number.isSafeInteger(Number(joinResponse?.revision))) {
        const revision = Number(joinResponse.revision);
        stateRevisionRef.current = revision;
        setStateRevision(revision);
      }
      setActiveSceneId(joinResponse?.activeSceneId ?? null);

      // Hydrate directly from the acknowledgement so a fresh/reconnected tab
      // cannot miss the authoritative scene when the server's immediate
      // combatState events race listener registration. The event handlers
      // remain in place for older servers and normal realtime updates.
      // date_of_change: 2026-08-25
      if (joinResponse?.state && typeof joinResponse.state === 'object') {
        const acknowledgedState = joinResponse.state;
        const acknowledgedRevision = Number(joinResponse?.revision);
        const acknowledgedMap = acknowledgedState.map;
        const promotedState = acknowledgedMap
          ? {
              ...acknowledgedState,
              gridSize: acknowledgedMap.gridSize ?? acknowledgedState.gridSize,
              gridWidth: acknowledgedMap.gridWidth ?? acknowledgedState.gridWidth,
              gridHeight: acknowledgedMap.gridHeight ?? acknowledgedState.gridHeight,
              width: acknowledgedMap.width ?? acknowledgedState.width,
              height: acknowledgedMap.height ?? acknowledgedState.height,
              tokens: acknowledgedMap.tokens ?? acknowledgedState.tokens,
              imageUrl: acknowledgedMap.imageUrl ?? acknowledgedState.imageUrl,
              fogOfWar: acknowledgedMap.fogOfWar ?? acknowledgedState.fogOfWar,
              tokenOwnership: acknowledgedMap.tokenOwnership ?? acknowledgedState.tokenOwnership,
              connectedPlayers: acknowledgedMap.connectedPlayers ?? acknowledgedState.connectedPlayers,
            }
          : acknowledgedState;
        setCombatState({
          ...promotedState,
          ...(joinResponse.rulesetRuntime ? { rulesetRuntime: joinResponse.rulesetRuntime } : {}),
          ...(Number.isSafeInteger(acknowledgedRevision) ? { _stateRevision: acknowledgedRevision } : {}),
          _forceAuthoritative: true,
          _authoritativeReason: 'join_ack',
        });
        setLastUpdate(Date.now());
      }

      if (joinResponse?.ok && joinResponse.role) {
        console.log('[WebSocketContext] User role set from WebSocket ACK:', joinResponse.role);
      } else {
        console.warn('[WebSocketContext] No role in ACK response:', joinResponse, '- defaulting to player');
      }

      setCurrentSessionId(sessionId);
      // @date_of_change: 2025-10-02 - Persist session ID for GM role persistence across refresh
      try {
        localStorage.setItem('current_session_id', sessionId);
      } catch (error) {
        console.warn('[WebSocketContext] Failed to cache session ID:', error);
      }
      // A metadata-only ACK proves membership but not that the saved actors are
      // ready. Keep the BattleRoom in its visible wait state until the routed
      // combat:state snapshot arrives. date_of_change: 2026-08-25
      if (joinResponse?.state && typeof joinResponse.state === 'object') {
        setSessionStatus('joined');
      }
      logWebSocket('Successfully joined session:', sessionId, 'as role:', joinResponse?.role || 'player');

      return true;
    } catch (error) {
      console.error('[WebSocketContext] Failed to join session:', error);
      setSessionStatus('error');
      throw error;
    }
    })();

    joinInFlightRef.current = {
      sessionId,
      promise: joinPromise
    };

    try {
      return await joinPromise;
    } finally {
      if (joinInFlightRef.current.sessionId === sessionId) {
        joinInFlightRef.current = { sessionId: null, promise: null };
      }
    }
  }, [isConnected, isConnecting, connect, webSocketService, updateRole, currentUser?.userId, currentSessionId, sessionStatus]);

  /**
   * Clean up session state (for explicit leaves or failed reconnections)
   * @date_of_change: 2025-10-01 - Extracted session cleanup for proper state management
   */
  const cleanupSessionState = useCallback(() => {
    logWebSocket('Cleaning up session state');
    setCurrentSessionId(null);
    // @date_of_change: 2025-10-02 - Clear persisted session ID when leaving
    try {
      localStorage.removeItem('current_session_id');
    } catch (error) {
      console.warn('[WebSocketContext] Failed to clear cached session ID:', error);
    }
    setSessionParticipants([]);
    setSessionStatus('idle');
    setCombatState(null);
    stateRevisionRef.current = 0;
    setStateRevision(0);
    setActiveSceneId(null);
    updateRole('player');
  }, [updateRole]);

  /**
   * Leave current combat session
   */
  const leaveSession = useCallback(async () => {
    if (!currentSessionId) return;

    logWebSocket('Leaving combat session:', currentSessionId);

    // The server is authoritative for membership. Preserve the attached room
    // and queued state when Leave fails so the UI cannot imply a deletion that
    // never committed. date_of_change: 2026-08-24
    const result = await leaveCombatSessionMembership(currentSessionId);
    logWebSocket('Backend leave successful:', result.message);
    if (result.newGM) {
      logWebSocket('GM role transferred to:', result.newGM);
    }

    webSocketService.leaveCombat();
    cleanupSessionState();
    return result;
  }, [currentSessionId, webSocketService, cleanupSessionState]);

  /**
   * Send combat action to other participants with 1s delay tolerance
   * @date_of_change 2025-09-24
   */
  const sendCombatAction = useCallback((actionData) => {
    if (!isConnected || !currentSessionId) {
      console.warn('[WebSocketContext] Cannot send action - not in a session or not connected');
      return false;
    }

    try {
      const actionMessage = {
        sessionId: currentSessionId,
        actorId: actionData.actorId,
        action: {
          ...actionData.action,
          // Include combat-critical data for synchronization
          roundNumber: actionData.roundNumber,
          turnIndex: actionData.turnIndex,
          actionType: actionData.actionType || 'action'
        },
        targets: actionData.targets || [],
        rollResults: actionData.rollResults || null,
        timestamp: Date.now(),
        userId: currentUser?.userId,
        sequenceId: `${currentUser?.userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      };

      logWebSocket('Sending combat action:', actionMessage);
      return webSocketService.emit('combatAction', actionMessage);
    } catch (error) {
      console.error('[WebSocketContext] Failed to send combat action:', error);
      return false;
    }
  }, [isConnected, currentSessionId, webSocketService, currentUser]);

  /**
   * Send initiative update to synchronize turn order
   * @date_of_change 2025-09-24
   */
  const sendInitiativeUpdate = useCallback((initiativeData) => {
    if (!currentSessionId) {
      console.warn('[WebSocketContext] Cannot send initiative update - not in a session');
      return false;
    }

    try {
      // @date_of_change: 2025-11-06
      // reason: Include isInitiativeConfirmed in the initiative payload so remote clients update
      // their local confirmation flag. Without this, players may have combatStarted=true but
      // isInitiativeConfirmed=false, which disables the "Finish My Turn" button despite correct ownership.
      const initiativeMessage = {
        sessionId: currentSessionId,
        combatants: initiativeData.combatants,
        currentTurnIndex: initiativeData.currentTurnIndex,
        round: initiativeData.round,
        combatStarted: initiativeData.combatStarted,
        // Propagate the confirmation flag when provided by the caller (GM/UI)
        isInitiativeConfirmed: initiativeData.isInitiativeConfirmed,
        timestamp: Date.now(),
        userId: currentUser?.userId
      };

      logWebSocket('Sending initiative update:', initiativeMessage);
      return webSocketService.emitMutation('initiativeUpdate', {
        ...initiativeMessage,
        mutationId: createRealtimeMutationId('initiativeUpdate', currentUser?.userId)
      });
    } catch (error) {
      console.error('[WebSocketContext] Failed to send initiative update:', error);
      return false;
    }
  }, [currentSessionId, webSocketService, currentUser]);

  /**
   * Send token update with conflict resolution for GM override
   * @date_of_change 2025-09-24
   */
  const sendTokenUpdate = useCallback((tokenData) => {
    if (!currentSessionId) {
      console.warn('[WebSocketContext] Cannot send token update - not in a session');
      return false;
    }

    try {
      const tokenMessage = {
        sessionId: currentSessionId,
        tokenId: tokenData.id,
        tokenData: {
          ...tokenData,
          lastModified: Date.now(),
          modifiedBy: currentUser?.userId
        },
        updateType: tokenData.updateType || 'position', // 'position', 'hp', 'properties', 'full'
        isGMOverride: tokenData.isGMOverride || false,
        timestamp: Date.now(),
        userId: currentUser?.userId
      };

      logWebSocket('Sending token update:', tokenMessage);
      return webSocketService.emitMutation('tokenUpdate', {
        ...tokenMessage,
        mutationId: createRealtimeMutationId('tokenUpdate', currentUser?.userId)
      });
    } catch (error) {
      console.error('[WebSocketContext] Failed to send token update:', error);
      return false;
    }
  }, [currentSessionId, webSocketService, currentUser]);

  /**
   * Send map update to other participants
   */
  const sendMapUpdate = useCallback((mapState, sessionIdOverride = null) => {
    const targetSessionId = sessionIdOverride || currentSessionId;

    if (!targetSessionId) {
      console.warn('[WebSocketContext] Cannot send map update - not in a session');
      return false;
    }

    try {
      return webSocketService.emitMutation('updateMap', {
        sessionId: targetSessionId,
        mapState: mapState,
        timestamp: Date.now(),
        userId: currentUser?.userId,
        mutationId: createRealtimeMutationId('updateMap', currentUser?.userId)
      });
    } catch (error) {
      console.error('[WebSocketContext] Failed to send map update:', error);
      return false;
    }
  }, [currentSessionId, webSocketService, currentUser]);

  /**
   * Send combat state update (GM only) - emits patch to be applied and broadcast
   * @date_of_change: 2025-09-26 - Added real-time combat state synchronization
   */
  const sendCombatUpdate = useCallback((patch) => {
    if (!currentSessionId) {
      console.warn('[WebSocketContext] Cannot send combat update - not in a session');
      return false;
    }

    if (userRole !== 'gm') {
      console.warn('[WebSocketContext] Cannot send combat update - not a GM');
      return false;
    }

    try {
      logWebSocket('Sending combat update:', { sessionId: currentSessionId, patch });
      return webSocketService.emitMutation('combat:update', {
        sessionId: currentSessionId,
        patch,
        timestamp: Date.now(),
        userId: currentUser?.userId,
        mutationId: createRealtimeMutationId('combat:update', currentUser?.userId)
      });
    } catch (error) {
      console.error('[WebSocketContext] Failed to send combat update:', error);
      return false;
    }
  }, [currentSessionId, webSocketService, currentUser, userRole]);

  /** Dedicated first-class domain mutation with queued revision/idempotency. */
  const sendBattleRoomMutation = useCallback((eventName, data = {}) => {
    if (!currentSessionId) return Promise.reject(new Error('Join a live BattleRoom before making this change.'));
    return webSocketService.emitMutationRequest(eventName, {
      ...data,
      sessionId: currentSessionId,
      sceneId: data.sceneId || activeSceneId,
      mutationId: data.mutationId || createRealtimeMutationId(eventName, currentUser?.userId),
    });
  }, [activeSceneId, currentSessionId, currentUser?.userId, webSocketService]);

  /** Read-only authoritative request such as movement or transition preview. */
  const requestBattleRoom = useCallback((eventName, data = {}) => {
    if (!currentSessionId) return Promise.reject(new Error('Join a live BattleRoom before making this request.'));
    return webSocketService.emitRequest(eventName, {
      ...data,
      sessionId: currentSessionId,
      sceneId: data.sceneId || activeSceneId,
    });
  }, [activeSceneId, currentSessionId, webSocketService]);

  /** Wait until earlier writes have acknowledged before resolving dice/actions. */
  const waitForBattleRoomMutationQueue = useCallback(() => {
    if (!currentSessionId) return Promise.reject(new Error('Join a live BattleRoom before resolving an action.'));
    return webSocketService.waitForMutationQueueIdle(currentSessionId);
  }, [currentSessionId, webSocketService]);

  /**
   * @date_of_change: 2025-10-05 - Generic WebSocket send function for any event type
   * Used by singleton managers like CombatLogManager that need WebSocket access
   */
  const sendWebSocketMessage = useCallback((eventName, data) => {
    if (!isConnected || !currentSessionId) {
      console.warn(`[WebSocketContext] Cannot send ${eventName} - not in a session or not connected`);
      return false;
    }

    try {
      const payload = {
        ...data,
        userId: currentUser?.userId
      };
      if (['updateMap', 'tokenUpdate', 'initiativeUpdate', 'combat:update', 'resolveAction'].includes(eventName)) {
        return webSocketService.emitMutation(eventName, {
          ...payload,
          sessionId: payload.sessionId || currentSessionId,
          mutationId: payload.mutationId || createRealtimeMutationId(eventName, currentUser?.userId)
        });
      }
      return webSocketService.emit(eventName, payload);
    } catch (error) {
      console.error(`[WebSocketContext] Failed to send ${eventName}:`, error);
      return false;
    }
  }, [isConnected, currentSessionId, webSocketService, currentUser]);

  /**
   * Add event listener for WebSocket events
   */
  const addEventListener = useCallback((event, callback) => {
    if (!eventListenersRef.current.has(event)) {
      eventListenersRef.current.set(event, new Set());
    }
    eventListenersRef.current.get(event).add(callback);

    // Add to WebSocket service
    webSocketService.on(event, callback);

    // Return cleanup function
    return () => {
      if (eventListenersRef.current.has(event)) {
        eventListenersRef.current.get(event).delete(callback);
      }
      webSocketService.off(event, callback);
    };
  }, [webSocketService]);

  /**
   * Set up WebSocket event listeners for multiplayer features
   */
  useEffect(() => {
    if (!isConnected) return;

    // Combat state updates with role information
    // @date_of_change: 2025-09-26 - Enhanced to handle role in combat state snapshots
    // @date_of_change: 2025-10-03 - Normalize fog data structure to top level
    // @date_of_change: 2025-10-04 - Accept modern payloads that include { state } without role (e.g., combat:update, resolveAction dual-emit)
    const handleCombatState = (payload) => {
      logWebSocket('Received combat state:', payload);

      let stateToSet;

      // Handle both formats:
      // - Legacy: direct state object
      // - Modern: { sessionId, state, role? }
      if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'state')) {
        if (payload.role) {
          logWebSocket('Role received in combat state:', payload.role);
          updateRole(payload.role, payload.sessionId || currentSessionId);
        }
        stateToSet = payload.rulesetRuntime
          ? { ...payload.state, rulesetRuntime: payload.rulesetRuntime }
          : payload.state;
        if (Number.isSafeInteger(Number(payload.revision))) {
          const revision = Number(payload.revision);
          stateRevisionRef.current = revision;
          setStateRevision(revision);
          webSocketService.setSessionRevision(payload.sessionId || currentSessionId, revision);
          // Carry the server revision with the snapshot itself. Consumers can
          // now prove the matching token collection has rendered instead of
          // treating the network ACK as UI settlement. date_of_change: 2026-08-31
          stateToSet = { ...stateToSet, _stateRevision: revision };
        }
        if (Object.prototype.hasOwnProperty.call(payload, 'activeSceneId')) {
          setActiveSceneId(payload.activeSceneId ?? null);
        }
        if (payload.forceAuthoritative) {
          stateToSet = { ...stateToSet, _forceAuthoritative: true, _authoritativeReason: payload.reason || 'server' };
        }
        if (Array.isArray(payload.combatLogHistory)) {
          stateToSet = { ...stateToSet, combatLogHistory: payload.combatLogHistory };
        }
      } else {
        // Legacy format: payload is the state itself
        stateToSet = payload;
      }

      // Promote map fields for useBattleRoom compatibility
      if (stateToSet?.map) {
        const promoted = {
          ...stateToSet,
          gridSize: stateToSet.map.gridSize ?? stateToSet.gridSize,
          gridWidth: stateToSet.map.gridWidth ?? stateToSet.gridWidth,
          gridHeight: stateToSet.map.gridHeight ?? stateToSet.gridHeight,
          width: stateToSet.map.width ?? stateToSet.width,
          height: stateToSet.map.height ?? stateToSet.height
        };

        if (stateToSet.map.fogOfWar && !stateToSet.fogOfWar) {
          promoted.fogOfWar = stateToSet.map.fogOfWar;
          console.log('[WebSocketContext] Promoted fog from map.fogOfWar to top level:', {
            fogEnabled: promoted.fogOfWar.enabled,
            fogOperations: promoted.fogOfWar.operations?.length || 0
          });
        }
        if (stateToSet.map.tokens && !stateToSet.tokens) {
          promoted.tokens = stateToSet.map.tokens;
          console.log('[WebSocketContext] Promoted tokens from map.tokens to top level:', {
            tokenCount: promoted.tokens.length
          });
        }
        if (stateToSet.map.imageUrl && !stateToSet.imageUrl) {
          promoted.imageUrl = stateToSet.map.imageUrl;
          console.log('[WebSocketContext] Promoted imageUrl from map.imageUrl to top level');
        }
        // @date_of_change: 2025-10-15 - Promote ownership/presence to top-level to unblock player control gating
        // Prefer the richer source (map.tokenOwnership) even if a shallow/empty top-level object exists
        if (stateToSet.map.tokenOwnership) {
          try {
            const incoming = stateToSet.map.tokenOwnership;
            const existing = stateToSet.tokenOwnership;
            const existingCount = existing && typeof existing === 'object' ? Object.keys(existing).length : 0;
            const incomingCount = incoming && typeof incoming === 'object' ? Object.keys(incoming).length : 0;
            promoted.tokenOwnership = (incomingCount >= existingCount) ? incoming : existing;
            console.log('[WebSocketContext] Promoted tokenOwnership from map to top level', {
              incomingCount,
              existingCount,
              used: (incomingCount >= existingCount) ? 'incoming(map.tokenOwnership)' : 'existing(top-level)'
            });
          } catch (e) {
            promoted.tokenOwnership = stateToSet.map.tokenOwnership;
            console.warn('[WebSocketContext] Promotion fallback for tokenOwnership due to error:', e);
          }
        }
        if (stateToSet.map.connectedPlayers && !stateToSet.connectedPlayers) {
          promoted.connectedPlayers = stateToSet.map.connectedPlayers;
          console.log('[WebSocketContext] Promoted connectedPlayers from map to top level:', promoted.connectedPlayers.length);
        }
        stateToSet = promoted;
      }

      setCombatState(stateToSet);
      setLastUpdate(Date.now());
      if (payload?.reason === 'join' || payload?._authoritativeReason === 'join') {
        setSessionStatus('joined');
      }
    };

    // Combat action synchronization with 1s delay tolerance
    // @date_of_change 2025-09-24
    const handleCombatAction = (actionData) => {
      const actionTimestamp = actionData.timestamp;
      const currentTime = Date.now();
      const timeDelta = Math.abs(currentTime - actionTimestamp);

      logWebSocket('Received combat action:', {
        action: actionData.action?.name,
        actorId: actionData.actorId,
        timeDelta,
        withinTolerance: timeDelta <= 1000,
        actionData
      });

      // Apply 1-second tolerance for action synchronization
      if (timeDelta <= 1000) {
        // Trigger combat action event for BattleRoom to handle
        window.dispatchEvent(new CustomEvent('websocket-combat-action', {
          detail: {
            actionData,
            timestamp: Date.now(),
            isRemoteAction: actionData.userId !== currentUser?.userId
          }
        }));
      } else {
        console.warn('[WebSocketContext] Combat action outside 1s tolerance, discarding:', {
          timeDelta,
          actionData
        });
      }
    };

    // Initiative order synchronization
    // @date_of_change 2025-09-24
    const handleInitiativeUpdate = (initiativeData) => {
      logWebSocket('Received initiative update:', initiativeData);
      if (Number.isSafeInteger(Number(initiativeData?.revision))) {
        const revision = Number(initiativeData.revision);
        stateRevisionRef.current = revision;
        setStateRevision(revision);
        webSocketService.setSessionRevision(initiativeData.sessionId || currentSessionId, revision);
      }
      // Trigger initiative update event for InitiativeTracker to handle
      window.dispatchEvent(new CustomEvent('websocket-initiative-update', {
        detail: {
          initiativeData,
          timestamp: Date.now(),
          isRemoteUpdate: initiativeData.userId !== currentUser?.userId
        }
      }));
    };

    // Token synchronization with GM override handling
    // @date_of_change 2025-09-24
    const handleTokenUpdate = (tokenData) => {
      logWebSocket('Received token update:', tokenData);
      if (Number.isSafeInteger(Number(tokenData?.revision))) {
        const revision = Number(tokenData.revision);
        stateRevisionRef.current = revision;
        setStateRevision(revision);
        webSocketService.setSessionRevision(tokenData.sessionId || currentSessionId, revision);
      }
      // Trigger token update event for BattleRoom to handle
      window.dispatchEvent(new CustomEvent('websocket-token-update', {
        detail: {
          tokenData,
          timestamp: Date.now(),
          isRemoteUpdate: tokenData.userId !== currentUser?.userId
        }
      }));
    };

    // Combat turn updates
    // @date_of_change: 2025-10-05 - Listen for 'combat:turn' derived events to reflect round/turn changes
    const handleCombatTurn = (turnData) => {
      logWebSocket('Received combat:turn:', turnData);

      // Update top-level combatState with round/turn/activeCombatant for consumers
      setCombatState(prev => ({
        ...prev,
        round: (typeof turnData.round === 'number') ? turnData.round : (prev?.round ?? prev?.round),
        turn: (typeof turnData.turnIndex === 'number') ? turnData.turnIndex : (prev?.turn ?? prev?.turn),
        activeCombatant: turnData.activeCombatantId ?? prev?.activeCombatant
      }));

      // Dispatch a window event so UI panels (e.g., InitiativeTracker/BattleRoom) can react
      window.dispatchEvent(new CustomEvent('websocket-combat-turn', {
        detail: {
          turnData,
          timestamp: Date.now()
        }
      }));
    };

    // @date_of_change: 2025-10-05 - Combat log entry synchronization
    // Receives combat log entries from GM and broadcasts to CombatLogManager
    const handleCombatLogEntry = (data) => {
      console.log('[WebSocketContext] Received combatLogEntry event:', {
        logEntry: data?.logEntry?.type,
        userId: data?.userId,
        currentUserId: currentUser?.userId,
        isRemote: data?.userId !== currentUser?.userId
      });
      logWebSocket('Received combat log entry:', data);

      // Dispatch window event for CombatLogManager to consume
      window.dispatchEvent(new CustomEvent('websocket-combat-log-entry', {
        detail: {
          logEntry: data.logEntry,
          sessionId: data.sessionId,
          timestamp: Date.now(),
          isRemote: data.userId !== currentUser?.userId
        }
      }));
    };

    // Map updates
    // @date_of_change: 2025-10-03 - Update combatState with fog and grid data from mapState
    const handleMapUpdate = (mapState) => {
      logWebSocket('Received map update:', mapState);
      if (Number.isSafeInteger(Number(mapState?._stateRevision))) {
        const revision = Number(mapState._stateRevision);
        stateRevisionRef.current = revision;
        setStateRevision(revision);
        webSocketService.setSessionRevision(currentSessionId, revision);
      }
      if (Object.prototype.hasOwnProperty.call(mapState || {}, '_activeSceneId')) {
        setActiveSceneId(mapState._activeSceneId ?? null);
      }
      // @date_of_change: 2025-10-04 - Removed spammy fog debug logging

      // Update combatState with fog and grid data so useBattleRoom can process them
      // useBattleRoom expects gridSize, width, height, fogOfWar at the top level
      setCombatState(prevState => {
        const chooseOwnership = () => {
          const a = mapState.tokenOwnership || {};
          const b = prevState?.tokenOwnership || {};
          const c = prevState?.map?.tokenOwnership || {};
          const count = (obj) => (obj && typeof obj === 'object') ? Object.keys(obj).length : 0;
          const aa = count(a), bb = count(b), cc = count(c);
          if (aa >= bb && aa >= cc) return a;
          if (bb >= aa && bb >= cc) return b;
          return c;
        };
        const chosenOwnership = chooseOwnership();
        const updatedState = {
          ...prevState,
          ...(Number.isSafeInteger(Number(mapState?._stateRevision))
            ? { _stateRevision: Number(mapState._stateRevision) }
            : {}),
          // Top-level fields for useBattleRoom processing
          gridSize: mapState.gridSize,
          width: mapState.width,
          height: mapState.height,
          fogOfWar: mapState.fogOfWar,
          tokens: mapState.tokens || prevState?.tokens || [],
          tokenOwnership: chosenOwnership,
          connectedPlayers: mapState.connectedPlayers || prevState?.connectedPlayers || [],
          // Also update map object for consistency
          map: {
            ...(prevState?.map || {}),
            gridSize: mapState.gridSize,
            width: mapState.width,
            height: mapState.height,
            fogOfWar: mapState.fogOfWar,
            tokens: mapState.tokens || prevState?.map?.tokens || [],
            tokenOwnership: chosenOwnership,
            connectedPlayers: mapState.connectedPlayers || prevState?.map?.connectedPlayers || prevState?.connectedPlayers || [],
            walls: mapState.walls || prevState?.map?.walls || [],
            doors: mapState.doors || prevState?.map?.doors || []
          }
        };

        return updatedState;
      });

      // Trigger map update event for components to handle
      window.dispatchEvent(new CustomEvent('websocket-map-update', {
        detail: { mapState, timestamp: Date.now() }
      }));

      // @date_of_change: 2025-10-04 - CRITICAL: Update lastUpdate timestamp so useBattleRoom processes changes
      // Without this, fog changes update combatState but lastUpdate stays stale, causing useEffect to skip
      setLastUpdate(Date.now());
    };

    // Session participants updates
    // @date_of_change: 2025-10-03 - Add deduplication and enhanced logging
    const handleParticipantsUpdate = (participants) => {
      console.log('[WebSocketContext] Received participants update:', JSON.stringify(participants, null, 2));
      console.log('[WebSocketContext] Participant details:', participants.map(p => ({
        userName: p.userName,
        userId: p.userId?.slice(-8),
        role: p.role,
        socketId: p.socketId
      })));

      // Deduplicate by userId in case backend sends duplicates
      const uniqueParticipants = participants.reduce((acc, p) => {
        const existing = acc.find(existing => existing.userId === p.userId);
        if (!existing) {
          acc.push(p);
        } else {
          console.warn('[WebSocketContext] Duplicate participant detected:', p.userName, p.userId);
        }
        return acc;
      }, []);

      console.log('[WebSocketContext] Setting unique participants:', uniqueParticipants.map(p => `${p.userName} (${p.role})`));
      setSessionParticipants(uniqueParticipants);
    };

    // User presence updates
    // @date_of_change: 2025-10-05 - Removed unused presence handler until backend emits 'userPresence'
    // Intentionally not implemented to avoid lint noise and confusion. Presence is covered by 'participantsUpdate'.

    // Error handling with auto-recovery for session not found
    // @date_of_change: 2025-09-25 - Added auto-recovery for session creation race conditions
    const handleError = async (error) => {
      console.error('[WebSocketContext] WebSocket error:', error);

      // Auto-recovery if join fails due to session not found
      if (String(error).includes('Combat session not found')) {
        console.warn('[WebSocketContext] Session not found - this may be a race condition during creation');
        // The session creation should have already been completed by the time we get here,
        // so we don't need to auto-create. The error indicates a deeper issue.
      }

      // You might want to show a notification to the user here
    };

    const handleMechanicalEvent = (event) => {
      if (!event?.id || !event?.type) return;
      const resolution = event?.data?.resolution;
      const attack = event?.data?.attack;
      const descriptions = {
        'damage:apply': resolution ? `${resolution.totalApplied} damage applied (${resolution.totalRolled} rolled).` : 'Damage applied.',
        'movement:commit': event?.data?.pausedForReaction ? 'Movement paused for a reaction.' : `Movement committed${Number.isFinite(Number(event?.data?.cost)) ? ` for ${Number(event.data.cost)} feet` : ''}.`,
        'reaction:resolve': attack ? `Opportunity attack ${attack.hit ? 'hit' : 'missed'}.` : 'Opportunity attack declined.',
        'concentration:start': 'Concentration started.',
        'concentration:resolve': event?.data?.roll ? `Concentration ${event.data.roll.success ? 'maintained' : 'broken'}.` : 'Concentration decision resolved.',
        'concentration:end': 'Concentration ended.',
        room_rewound: event?.data?.reason ? `BattleRoom rewound: ${event.data.reason}` : 'BattleRoom rewound.',
        scene_transitioned: 'The table moved to a new scene.',
      };
      const type = event.type === 'damage:apply'
        ? 'DAMAGE_DEALT'
        : event.type === 'reaction:resolve' ? 'ACTION_REACTION' : 'ACTION_STANDARD';
      const logEntry = {
        id: event.id,
        type,
        round: event.round,
        turn: event.turn,
        combatantId: event.actorId,
        timestamp: event.createdAt,
        description: descriptions[event.type] || String(event.type).replace(/[:_]/g, ' '),
        data: {
          mechanicalEventType: event.type,
          ...event.data,
          result: resolution ? {
            damage: resolution.totalApplied,
            totalDamage: resolution.totalApplied,
            targetHpBefore: resolution.hpBefore,
            targetHpRemaining: resolution.hpAfter,
            damageResolution: resolution,
            damageDetails: resolution.components,
          } : (attack ? { attackRoll: attack, hit: attack.hit, damage: attack.damageApplied, damageResolution: attack.damageResolution } : event.data),
        },
      };
      window.dispatchEvent(new CustomEvent('websocket-combat-log-entry', {
        detail: { logEntry, sessionId: currentSessionId, timestamp: Date.now(), isRemote: true },
      }));
    };

    const handleRealtimeConflict = (payload) => {
      console.warn('[WebSocketContext] Realtime conflict recovered from authoritative state:', payload?.revision);
      if (Number.isSafeInteger(Number(payload?.revision))) {
        const revision = Number(payload.revision);
        stateRevisionRef.current = revision;
        setStateRevision(revision);
        webSocketService.setSessionRevision(currentSessionId, revision);
      }
      if (payload?.state) {
        setCombatState({
          ...payload.state,
          ...(payload.rulesetRuntime ? { rulesetRuntime: payload.rulesetRuntime } : {}),
          ...(Number.isSafeInteger(Number(payload?.revision)) ? { _stateRevision: Number(payload.revision) } : {}),
          _forceAuthoritative: true,
          _authoritativeReason: 'conflict_recovery'
        });
        setLastUpdate(Date.now());
      }
      window.dispatchEvent(new CustomEvent('battleroom-realtime-recovered', { detail: payload }));
    };

    const handleMutationAck = (event) => {
      const detail = event?.detail || {};
      if (Number.isSafeInteger(Number(detail.revision))) {
        const revision = Number(detail.revision);
        stateRevisionRef.current = revision;
        setStateRevision(revision);
        webSocketService.setSessionRevision(detail.sessionId || currentSessionId, revision);
      }
    };

    const handleSessionMembershipLost = (event) => {
      const affectedSessionId = String(event?.detail?.sessionId || '');
      if (!affectedSessionId || affectedSessionId !== String(currentSessionId || '')) return;
      // The transport is connected but no longer belongs to its Socket.IO
      // room. Re-enter the normal visible rejoin state; the queued mutation is
      // held by WebSocketService until joinCombat acknowledges membership.
      // date_of_change: 2026-08-25
      setSessionStatus('reconnecting');
    };


    // Handle reconnection failure - only then clean up session state
    // @date_of_change: 2025-10-01 - Listen for complete reconnection failure
    const handleReconnectionFailed = (event) => {
      console.error('[WebSocketContext] All reconnection attempts failed:', event.detail);
      logWebSocket('Reconnection completely failed, cleaning up session state');
      cleanupSessionState();
    };

    // Note: handleCombatState already handles both initial snapshots and real-time updates
    // @date_of_change: 2025-09-26 - The existing handleCombatState correctly replaces state for all combat:state events
    // @date_of_change: 2025-10-04 - Listen to modern 'combat:state' snapshots (emitted on join and GM combat:update)

    // Add event listeners
    const cleanupListeners = [
      addEventListener('combatState', handleCombatState),
      addEventListener('combat:state', handleCombatState),
      addEventListener('combatAction', handleCombatAction),
      addEventListener('initiativeUpdate', handleInitiativeUpdate),
      addEventListener('combat:turn', handleCombatTurn),
      addEventListener('combatLogEntry', handleCombatLogEntry),
      addEventListener('mechanical:event', handleMechanicalEvent),
      addEventListener('tokenUpdate', handleTokenUpdate),
      addEventListener('mapUpdate', handleMapUpdate),
      addEventListener('participantsUpdate', handleParticipantsUpdate),
      addEventListener('realtime:conflict', handleRealtimeConflict),
      // @date_of_change: 2025-10-04 - Backend does not emit 'userPresence' yet; gate to reduce noise
      // addEventListener('userPresence', handleUserPresence),
      // Note: We now subscribe to both legacy 'combatState' and modern 'combat:state' events
      addEventListener('error', handleError)
    ];

    // Add window event listener for reconnection failure
    window.addEventListener('websocket-reconnection-failed', handleReconnectionFailed);
    window.addEventListener('realtime-mutation-ack', handleMutationAck);
    window.addEventListener('websocket-session-membership-lost', handleSessionMembershipLost);

    // Cleanup function
    return () => {
      cleanupListeners.forEach(cleanup => cleanup());
      // @date_of_change: 2025-10-01 - Clean up window event listener
      window.removeEventListener('websocket-reconnection-failed', handleReconnectionFailed);
      window.removeEventListener('realtime-mutation-ack', handleMutationAck);
      window.removeEventListener('websocket-session-membership-lost', handleSessionMembershipLost);
    };
  }, [isConnected, addEventListener, cleanupSessionState, updateRole, currentUser?.userId, currentSessionId, webSocketService]);

  /**
   * Auto-rejoin session on page load/reconnect if session ID is persisted
   * @date_of_change: 2025-10-03 - CRITICAL FIX: Auto-rejoin persisted sessions
   * When page refreshes, currentSessionId is loaded from localStorage but joinSession() is never called.
   * This causes participant list desync because backend never receives joinCombat event.
   */
  useEffect(() => {
    // Only auto-rejoin if:
    // 1. WebSocket is connected
    // 2. We have a persisted session ID
    // 3. Status is 'idle' or 'reconnecting' (not 'joining' or 'joined')
    const shouldAutoRejoin = isConnected && currentSessionId && (sessionStatus === 'idle' || sessionStatus === 'reconnecting');

    if (shouldAutoRejoin) {
      logWebSocket('Auto-rejoining persisted session:', currentSessionId, 'Status:', sessionStatus);

      // Auto-rejoin the persisted session
      joinSession(currentSessionId).catch(error => {
        console.error('[WebSocketContext] Auto-rejoin failed:', error);
        // If auto-rejoin fails, clean up the stale session state
        cleanupSessionState();
      });
    }
  }, [isConnected, currentSessionId, sessionStatus, joinSession, cleanupSessionState]);

  /**
   * Clean up on session leave or disconnect
   * @date_of_change: 2025-10-01 - CRITICAL FIX: Don't clear session state during temporary disconnects
   * This was causing GM->Player role corruption during map uploads and reconnections
   */
  useEffect(() => {
    if (!isConnected && currentSessionId) {
      // @date_of_change: 2025-10-01 - Only clear session state after reconnection completely fails
      // DO NOT clear session state on temporary disconnections (map uploads, network hiccups)
      // The WebSocketService will handle reconnection with preserved authentication

      logWebSocket('Connection lost, preserving session state for reconnection');

      // Set status to indicate we're disconnected but attempting to reconnect
      setSessionStatus('reconnecting');

      // DO NOT clear currentSessionId, role, or other session state here
      // Only clear if reconnection fails completely after multiple attempts
    }
  }, [isConnected, currentSessionId]);

  // Context value
  const contextValue = {
    // Connection state
    webSocketStatus,
    isConnected,
    isConnecting,
    authError,
    isAuthLoaded,

    // Connection methods
    connect,
    disconnect,
    reconnect,

    // User information
    currentUser,
    isSignedIn,

    // Role information from WebSocket-based role detection
    // @date_of_change: 2025-09-26 - Added WebSocket-based role information
    userRole, // 'gm' | 'player' | null
    isGameMaster: isGameMaster, // Already computed by useSessionRole hook

    // Session management
    currentSessionId,
    sessionParticipants,
    sessionStatus,
    joinSession,
    leaveSession,
    // date_of_change: 2026-04-17 - Expose synchronous state-only cleanup for new-room navigation
    clearSessionState: cleanupSessionState,
    // date_of_change: 2026-04-17 - Expose updateRole so callers can set GM role on dashboard-initiated loads
    updateRole,

    // Real-time data
    combatState,
    lastUpdate,
    stateRevision,
    activeSceneId,
    pendingMutationCount,

    // Communication methods
    sendCombatAction,
    sendInitiativeUpdate,
    sendTokenUpdate,
    sendMapUpdate,
    sendCombatUpdate, // @date_of_change: 2025-09-26 - Added real-time combat state sync
    sendBattleRoomMutation,
    waitForBattleRoomMutationQueue,
    requestBattleRoom,
    sendWebSocketMessage, // @date_of_change: 2025-10-05 - Generic WebSocket send for any event type
    addEventListener,

    // WebSocket service reference (for advanced usage)
    webSocketService
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

/**
 * Hook to use WebSocket context
 * Provides easy access to WebSocket functionality in components
 */
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);

  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }

  return context;
};

/**
 * Optional form for reusable panels that can also render in editor previews
 * and isolated component tests. Live actions remain disabled until a provider
 * supplies an authenticated BattleRoom connection.
 */
export const useOptionalWebSocket = () => useContext(WebSocketContext);

/**
 * Hook for session-specific WebSocket operations
 * Provides combat session specific functionality
 */
export const useMultiplayerSession = () => {
  const {
    currentSessionId,
    sessionParticipants,
    sessionStatus,
    joinSession,
    leaveSession,
    combatState,
    lastUpdate,
    sendCombatAction,
    sendInitiativeUpdate,
    sendTokenUpdate,
    sendMapUpdate,
    sendCombatUpdate,
    sendBattleRoomMutation,
    isConnected,
    currentUser,
    // @date_of_change: 2025-09-26 - Added WebSocket-based role information to multiplayer session hook
    userRole,
    isGameMaster
  } = useWebSocket();

  const isInSession = !!currentSessionId && (sessionStatus === 'joined' || sessionStatus === 'reconnecting');

  return {
    // Session state
    currentSessionId,
    sessionParticipants,
    sessionStatus,
    isInSession,
    isConnected,

    // Session methods
    joinSession,
    leaveSession,

    // Real-time data
    combatState,
    lastUpdate,

    // User information
    currentUser,

    // Role information from WebSocket-based role detection
    // @date_of_change: 2025-09-26 - Added WebSocket-based role information to multiplayer session hook return
    userRole, // 'gm' | 'player' | null
    isGameMaster,

    // Communication with 1s delay tolerance for combat actions
    sendCombatAction,
    sendInitiativeUpdate,
    sendTokenUpdate,
    sendMapUpdate,
    sendCombatUpdate, // @date_of_change: 2025-09-26 - Added real-time combat state sync
    // First-class mechanics consumers must use the same revisioned/idempotent
    // domain mutation path as direct BattleRoom components.
    // date_of_change: 2026-08-26
    sendBattleRoomMutation
  };
};

export default WebSocketContext;

// @date_of_change: 2025-09-30
// reason: Provide a single source of truth for 'role' across the app with debugging
export function useCombatRole() {
  const ctx = useContext(WebSocketContext);

  // Debug logging to trace role detection issue
  useEffect(() => {
    console.log('[useCombatRole] DEBUG - Context values:', {
      userRole: ctx?.userRole,
      isGameMaster: ctx?.isGameMaster,
      currentSessionId: ctx?.currentSessionId,
      sessionStatus: ctx?.sessionStatus,
      contextExists: !!ctx
    });
  }, [ctx]);

  // ctx.userRole should be 'gm' | 'player' | null (until ACK)
  return {
    role: ctx?.userRole,
    isGM: ctx?.userRole === 'gm'
  };
}
